import { useState, useCallback, useEffect, useRef } from "react";
import { usePhotoboothOptional, usePhotoboothPerson } from "./PhotoboothContext";
import UploadEntryContent from "./UploadEntryContent";
import CameraContent from "./CameraContent";
import PreviewContent, { type PreviewContentRef } from "./PreviewContent";
import { registerBgRemovalCacheWorker } from "@/pages/photobooth/useBgRemovalCache";
import type { PhotoboothCaptureMethod } from "@/pages/photobooth/_utils/amplitude";
import Progress from "@/components/Progress";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import IconButton from "@/components/IconButton";

type Screen = "upload-entry" | "camera" | "preview";

export default function PhotoboothModal() {
  const photoboothContext = usePhotoboothOptional();
  const { person, loading } = usePhotoboothPerson();

  const [screen, setScreen] = useState<Screen>("upload-entry");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [captureMethod, setCaptureMethod] = useState<PhotoboothCaptureMethod>("upload");
  const [isSaving, setIsSaving] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const previewRef = useRef<PreviewContentRef>(null);
  const allowCloseRef = useRef(false);

  const isOpen = photoboothContext?.modalState.isOpen ?? false;
  const initialMode = photoboothContext?.modalState.initialMode ?? "upload";
  const analyticsSource = photoboothContext?.modalState.analyticsSource ?? "person_avatar";
  const closePhotobooth = photoboothContext?.closePhotobooth;

  useEffect(() => {
    if (isOpen) {
      registerBgRemovalCacheWorker();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setScreen(initialMode === "camera" ? "camera" : "upload-entry");
      setCapturedImage(null);
      setCaptureMethod(initialMode === "camera" ? "camera" : "upload");
      setIsSaving(false);
      setShowExitConfirm(false);
      allowCloseRef.current = false;
    }
  }, [isOpen, initialMode]);

  const handleStartCamera = useCallback(() => {
    setScreen("camera");
  }, []);

  const handleCapturePhoto = useCallback((imageData: string, method: PhotoboothCaptureMethod) => {
    setCaptureMethod(method);
    setCapturedImage(imageData);
    setScreen("preview");
  }, []);

  const handleCaptureFromUpload = useCallback(
    (imageData: string) => {
      handleCapturePhoto(imageData, "upload");
    },
    [handleCapturePhoto]
  );

  const handleBackToUpload = useCallback(() => {
    setCapturedImage(null);
    setScreen("upload-entry");
  }, []);

  const closeWithoutConfirmation = useCallback(() => {
    allowCloseRef.current = true;
    setShowExitConfirm(false);
    closePhotobooth?.();
  }, [closePhotobooth]);

  const handleRequestClose = useCallback(() => {
    if (allowCloseRef.current) {
      allowCloseRef.current = false;
      return;
    }

    if (isSaving) {
      return;
    }

    if (screen === "preview" && previewRef.current?.hasUnsavedEdits) {
      setShowExitConfirm(true);
      return;
    }

    closeWithoutConfirmation();
  }, [closeWithoutConfirmation, isSaving, screen]);

  const handleSaveComplete = useCallback(() => {
    setIsSaving(false);
    closeWithoutConfirmation();
  }, [closeWithoutConfirmation]);

  const handleSavePhoto = useCallback(async () => {
    if (previewRef.current) {
      setIsSaving(true);
      try {
        await previewRef.current.save();
      } catch {
        setIsSaving(false);
      }
    }
  }, []);

  const handleRetakeFromPreview = useCallback(() => {
    setCapturedImage(null);
    setScreen("upload-entry");
  }, []);

  if (!photoboothContext) {
    return null;
  }

  const getHeader = () => {
    switch (screen) {
      case "camera":
        return {
          title: "Take Photo",
          onBack: handleBackToUpload
        };
      case "preview":
        return {
          title: "Preview Photo",
          action: (
            <IconButton
              name="close"
              ariaLabel="Exit photobooth"
              title="Exit photobooth"
              onClick={handleRequestClose}
              disabled={isSaving}
            />
          )
        };
      default:
        return {
          title: "Update Profile Photo"
        };
    }
  };

  const getActions = () => {
    if (screen === "camera") {
      return null;
    }
    if (screen === "preview") {
      return (
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outlined" onClick={handleRetakeFromPreview} icon="refresh" testId="retake-button">
            Start Over
          </Button>
          <Button
            variant="filled"
            icon={isSaving ? undefined : "check"}
            onClick={handleSavePhoto}
            disabled={isSaving}
            testId="save-button"
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <Progress />
                Saving...
              </span>
            ) : (
              "Save to Profile"
            )}
          </Button>
        </div>
      );
    }
    return (
      <Button variant="text" onClick={handleRequestClose} testId="photobooth-cancel-button">
        Cancel
      </Button>
    );
  };

  const getDialogClassName = () => {
    switch (screen) {
      case "camera":
        return "w-[600px] max-w-[95vw] max-h-[90vh]";
      case "preview":
        return "w-[95vw] max-w-[1200px] max-h-[90vh]";
      default:
        return "w-[500px] max-w-[90vw] max-h-[90vh]";
    }
  };

  const renderContent = () => {
    if (loading || !person) {
      return (
        <div className="flex items-center justify-center py-12">
          <Progress />
        </div>
      );
    }

    switch (screen) {
      case "camera":
        return <CameraContent onCapture={handleCapturePhoto} onBack={handleBackToUpload} />;
      case "preview":
        return capturedImage ? (
          <PreviewContent
            ref={previewRef}
            person={person}
            imageData={capturedImage}
            onRetake={handleRetakeFromPreview}
            onSaveComplete={handleSaveComplete}
            analyticsSource={analyticsSource}
            captureMethod={captureMethod}
            showActionButtons={false}
            layout="responsive"
          />
        ) : null;
      default:
        return (
          <UploadEntryContent person={person} onCapture={handleCaptureFromUpload} onStartCamera={handleStartCamera} />
        );
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClosed={handleRequestClose}
        header={getHeader()}
        hasFormElements
        className={getDialogClassName()}
        actions={getActions()}
        dismissOnOutsideClick={false}
        disableClose={isSaving}
      >
        {renderContent()}
      </Dialog>
      <Dialog
        open={showExitConfirm}
        onClosed={() => setShowExitConfirm(false)}
        headline="Unsaved Photo Edits"
        className="w-[520px] max-w-[90vw]"
        actions={
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="text" onClick={() => setShowExitConfirm(false)} testId="continue-editing-button">
              Continue Editing
            </Button>
            <Button
              variant="filled"
              theme="error"
              onClick={closeWithoutConfirmation}
              testId="exit-without-saving-button"
            >
              Exit Without Saving
            </Button>
          </div>
        }
      >
        <p className="text-body-large text-sys-brand-on-surface">
          You have photo edits that have not been saved to this profile yet. Continue editing to keep working, or exit
          now and lose those unsaved changes.
        </p>
      </Dialog>
    </>
  );
}
